import {
	eventLogDb,
	onEventLogAppend,
	type ReaderEvent,
	type StatsArticleRow,
	type StatsDayRow,
	type StatsHourFigures,
	type StatsIssueRow,
} from '@shared/events';
import { readerStatsPreference } from '@shared/lib/reader-preferences';
import { withTabLock } from '@shared/lib/tab-lock';

/**
 * The reader's own reading figures: a projection of the event log, kept on
 * the device and never sent anywhere. Three small tables, all derived from
 * events, and kept after the events themselves expire:
 *
 * - `statsDays`: per calendar day, time read and listened, articles and
 *   issues finished. Streaks and weekly or yearly figures are computed from
 *   these when asked (`read.ts`), not stored.
 * - `statsIssues`: per issue, time read and how far through it the reader is.
 * - `statsArticles`: per article within an issue, time read, deepest scroll
 *   and when it was finished.
 *
 * It reads the log from its own cursor, like a destination, so it works for
 * a reader who shares no analytics, catches up after being off, and can be
 * rebuilt from the retained 30 days. It is independent of the sharing
 * setting and has its own (Settings → Privacy → Reading stats).
 */
export const READER_STATS_CONSUMER = 'reader-stats';
const PAGE_SIZE = 200;

/** `YYYY-MM-DD` in the device's own time zone: a reader's "today". */
export function dayOf(timestamp: number): string {
	const date = new Date(timestamp);
	const pad = (value: number) => String(value).padStart(2, '0');
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

type Loose = Record<string, unknown>;
const num = (value: unknown): number => (typeof value === 'number' ? value : 0);

class Batch {
	days = new Map<string, StatsDayRow>();
	issues = new Map<number, StatsIssueRow>();
	articles = new Map<string, StatsArticleRow>();

	async day(timestamp: number): Promise<StatsDayRow> {
		const day = dayOf(timestamp);
		let row = this.days.get(day);
		if (!row) {
			row = (await eventLogDb.statsDays.get(day)) ?? {
				day,
				readMs: 0,
				listenedMs: 0,
				articlesCompleted: 0,
				issuesCompleted: 0,
			};
			this.days.set(day, row);
		}
		return row;
	}

	/** Adds to a figure of the day and of the hour `timestamp` falls in. */
	async add(
		timestamp: number,
		figure: keyof StatsHourFigures,
		amount: number,
	): Promise<void> {
		const row = await this.day(timestamp);
		row[figure] += amount;
		const hours = (row.hours ??= {});
		const hour = (hours[new Date(timestamp).getHours()] ??= {
			readMs: 0,
			listenedMs: 0,
			articlesCompleted: 0,
			issuesCompleted: 0,
		});
		hour[figure] += amount;
	}

	async issue(issueId: number, timestamp: number): Promise<StatsIssueRow> {
		let row = this.issues.get(issueId);
		if (!row) {
			row = (await eventLogDb.statsIssues.get(issueId)) ?? {
				issueId,
				readMs: 0,
				unitsSeen: 0,
				unitsTotal: 0,
				lastReadAt: timestamp,
			};
			this.issues.set(issueId, row);
		}
		row.lastReadAt = Math.max(row.lastReadAt, timestamp);
		return row;
	}

	async article(
		issueId: number,
		articleId: number,
		timestamp: number,
	): Promise<StatsArticleRow> {
		const key = `${issueId}:${articleId}`;
		let row = this.articles.get(key);
		if (!row) {
			row = (await eventLogDb.statsArticles.get([issueId, articleId])) ?? {
				issueId,
				articleId,
				readMs: 0,
				maxDepth: 0,
				lastReadAt: timestamp,
			};
			this.articles.set(key, row);
		}
		row.lastReadAt = Math.max(row.lastReadAt, timestamp);
		return row;
	}

	async save(): Promise<void> {
		await eventLogDb.statsDays.bulkPut([...this.days.values()]);
		await eventLogDb.statsIssues.bulkPut([...this.issues.values()]);
		await eventLogDb.statsArticles.bulkPut([...this.articles.values()]);
	}
}

/**
 * Sets the issue's progress to the furthest the reader has got in any one
 * layout, or what its completion stated if that is further.
 */
function furthest(issue: StatsIssueRow): void {
	const candidates = [
		...Object.values(issue.byLayout ?? {}).map((counted) => ({
			seen: counted.units.length,
			total: counted.total,
		})),
		...(issue.completedUnits ? [issue.completedUnits] : []),
	].filter((candidate) => candidate.total > 0);
	const best = candidates.sort(
		(a, b) => b.seen / b.total - a.seen / a.total || b.total - a.total,
	)[0];
	if (best) {
		issue.unitsSeen = best.seen;
		issue.unitsTotal = best.total;
	}
}

/** What one event adds to the reader's figures. Anything else adds nothing. */
async function apply(batch: Batch, event: ReaderEvent): Promise<void> {
	const fields = event as unknown as Loose;
	const at = event.event_ts;
	const issueId = event.issue_id;
	const articleId = event.article_id;

	switch (event.name as string) {
		case 'issue_open': {
			if (issueId !== undefined) {
				const issue = await batch.issue(issueId, at);
				issue.title = event.issue_title ?? issue.title;
			}
			return;
		}
		case 'article_open':
		case 'issue_page_view': {
			const unit = event.page_number;
			const layout = event.content_type;
			if (issueId === undefined || unit === undefined || !layout) {
				return;
			}
			const issue = await batch.issue(issueId, at);
			issue.title = event.issue_title ?? issue.title;
			const byLayout = (issue.byLayout ??= {});
			const counted = (byLayout[layout] ??= { units: [], total: 0 });
			if (!counted.units.includes(unit)) {
				counted.units.push(unit);
			}
			// The size the newest event states; an issue can gain pages.
			counted.total = num(fields.units_total) || counted.total;
			furthest(issue);
			return;
		}
		case 'article_read': {
			const readMs = num(fields.dwell_ms);
			await batch.add(at, 'readMs', readMs);
			if (issueId === undefined) {
				return;
			}
			const issue = await batch.issue(issueId, at);
			issue.readMs += readMs;
			issue.title = event.issue_title ?? issue.title;
			if (articleId !== undefined) {
				const article = await batch.article(issueId, articleId, at);
				article.readMs += readMs;
				article.maxDepth = Math.max(article.maxDepth, num(fields.max_scroll_depth));
				article.title = event.article_title ?? article.title;
			}
			return;
		}
		case 'issue_page_leave': {
			// Both pages of a spread report the whole time it was shown: the
			// reader spent that time once.
			const readMs = num(fields.dwell_ms) / (num(fields.pages_visible) || 1);
			await batch.add(at, 'readMs', readMs);
			if (issueId !== undefined) {
				(await batch.issue(issueId, at)).readMs += readMs;
			}
			return;
		}
		case 'article_complete': {
			await batch.add(at, 'articlesCompleted', 1);
			if (issueId !== undefined && articleId !== undefined) {
				const article = await batch.article(issueId, articleId, at);
				article.completedAt ??= at;
				article.title = event.article_title ?? article.title;
			}
			return;
		}
		case 'issue_complete': {
			await batch.add(at, 'issuesCompleted', 1);
			if (issueId !== undefined) {
				const issue = await batch.issue(issueId, at);
				issue.completedAt ??= at;
				issue.completedUnits = {
					seen: num(fields.units_seen),
					total: num(fields.units_total),
				};
				furthest(issue);
				issue.title = event.issue_title ?? issue.title;
			}
			return;
		}
		case 'audio_pause':
		case 'audio_complete': {
			await batch.add(at, 'listenedMs', num(fields.listened_ms));
			return;
		}
		default:
	}
}

async function lastSeq(): Promise<number> {
	return (await eventLogDb.events.orderBy('seq').last())?.seq ?? 0;
}

async function projectPending(): Promise<void> {
	for (;;) {
		const more = await eventLogDb.transaction(
			'rw',
			[
				eventLogDb.events,
				eventLogDb.cursors,
				eventLogDb.statsDays,
				eventLogDb.statsIssues,
				eventLogDb.statsArticles,
			],
			async () => {
				const cursor =
					(await eventLogDb.cursors.get(READER_STATS_CONSUMER))?.seq ?? 0;
				// Off: the figures stay as they are and nothing recorded
				// meanwhile is ever added to them.
				if (readerStatsPreference.get() === 'off') {
					const end = await lastSeq();
					if (end > cursor) {
						await eventLogDb.cursors.put({
							consumer: READER_STATS_CONSUMER,
							seq: end,
						});
					}
					return false;
				}
				const rows = await eventLogDb.events
					.where('seq')
					.above(cursor)
					.limit(PAGE_SIZE)
					.toArray();
				if (!rows.length) {
					return false;
				}
				const batch = new Batch();
				for (const row of rows) {
					await apply(batch, row.event);
				}
				await batch.save();
				// In the same transaction as the figures: an event is counted
				// exactly once, whatever happens to the page.
				await eventLogDb.cursors.put({
					consumer: READER_STATS_CONSUMER,
					seq: rows[rows.length - 1].seq as number,
				});
				return rows.length === PAGE_SIZE;
			},
		);
		if (!more) {
			return;
		}
	}
}

let running: Promise<void> | undefined;
let again = false;

/** Brings the figures up to date with the log. One tab at a time. */
export function projectReaderStats(): Promise<void> {
	if (running) {
		again = true;
		return running;
	}
	running = (async () => {
		do {
			again = false;
			try {
				await withTabLock('cf-reader-stats', projectPending);
			} catch {
				// No usable database: there are no stats on this device.
			}
		} while (again);
	})().finally(() => {
		running = undefined;
	});
	return running;
}

let stop: (() => void) | undefined;

/** Keeps the figures up to date from now on. Call once at boot. */
export function startReaderStats(): void {
	if (stop) {
		return;
	}
	stop = onEventLogAppend(() => void projectReaderStats());
	void projectReaderStats();
}

async function clearTables(cursorSeq: number): Promise<void> {
	await eventLogDb.transaction(
		'rw',
		[
			eventLogDb.events,
			eventLogDb.cursors,
			eventLogDb.statsDays,
			eventLogDb.statsIssues,
			eventLogDb.statsArticles,
		],
		async () => {
			await eventLogDb.statsDays.clear();
			await eventLogDb.statsIssues.clear();
			await eventLogDb.statsArticles.clear();
			await eventLogDb.cursors.put({
				consumer: READER_STATS_CONSUMER,
				seq: cursorSeq < 0 ? await lastSeq() : cursorSeq,
			});
		},
	);
}

/**
 * The reader's "clear my reading stats": deletes the figures and starts
 * again from now. The event log is untouched, so events still waiting to be
 * sent to the publisher's analytics are sent as usual; that is a separate
 * setting.
 */
export async function clearReaderStats(): Promise<void> {
	await running;
	try {
		await clearTables(-1);
	} catch {
		// Nothing durable to clear.
	}
}

/**
 * Recomputes the figures from the events still in the log (up to 30 days).
 * Anything older is lost by doing this, so it is for a changed definition
 * or a repair, not for routine use.
 */
export async function rebuildReaderStats(): Promise<void> {
	await running;
	await clearTables(0);
	await projectReaderStats();
}

/** Test-only. */
export function stopReaderStatsForTests(): void {
	stop?.();
	stop = undefined;
}
